import type { BaseDocument } from '../mongo/base-document.interface';
import type { SyncResource } from '../sync/sync-resource';

export const CARRIER_TYPE = {
  LOCAL: 'LOCAL',
  NATIONAL: 'NATIONAL',
} as const;

export type CarrierType = (typeof CARRIER_TYPE)[keyof typeof CARRIER_TYPE];

export interface CarrierSiteSpecific {
  disabled?: boolean;
  from?: Date | null;
  siteId: string;
  to?: Date | null;
}

export interface CarrierEnabledScope {
  enabledOnAllSites?: boolean;
  from?: Date | null;
  siteSpecifics?: CarrierSiteSpecific[];
  spaceId: string;
  to?: Date | null;
}

export interface Carrier extends BaseDocument {
  code: string;
  enabledScopes: CarrierEnabledScope[];
  integration: boolean;
  name: string;
  spaceId?: string;
}

/**
 * Minimal Carrier reference embedded (denormalized) into other documents.
 */
export type CarrierSummary = Pick<Carrier, 'id' | 'code' | 'name'>;

export type CreateCarrier = Omit<Carrier, keyof BaseDocument | 'integration'>;

export type UpdateCarrier = Partial<CreateCarrier>;

export const SITE_CARRIER_STATUS = {
  ENABLED: 'enabled',
  EXPIRED: 'expired',
  SCHEDULED: 'scheduled',
} as const;

export type SiteCarrierStatus = (typeof SITE_CARRIER_STATUS)[keyof typeof SITE_CARRIER_STATUS];

/**
 * Granularity for resolving carrier-branch visibility (e.g. `VehicleService.search`):
 * `site` checks enablement on the given site(s); `space` checks that the carrier is referenced
 * on the space — either via an `enabledScopes` entry for that space, or a local carrier whose
 * top-level `spaceId` is that space — regardless of site enablement.
 * Must match `DriverVehicleLinkService.validateDriverDefaultVehicle`'s space-level rule when used
 * for the default-vehicle picker, or an assignable vehicle can vanish from the list.
 */
export const CARRIER_ENABLEMENT_SCOPE = {
  SITE: 'site',
  SPACE: 'space',
} as const;

export type CarrierEnablementScope = (typeof CARRIER_ENABLEMENT_SCOPE)[keyof typeof CARRIER_ENABLEMENT_SCOPE];

// ─── Cascade (archive / unreference) ──────────────────────────────────────────

/** How a carrier action (archive/unreference) handles active driver/vehicle relations. */
export const CARRIER_CASCADE_MODE = {
  ACKNOWLEDGE: 'acknowledge',
  CASCADE: 'cascade',
  PREVENT: 'prevent',
} as const;

export type CarrierCascadeMode = (typeof CARRIER_CASCADE_MODE)[keyof typeof CARRIER_CASCADE_MODE];

/** The carrier action a cascade is triggered by — reused as the `action` in `AuditCascadeMarker`. */
export const CARRIER_CASCADE_ACTION = {
  ARCHIVE: 'archive',
  UNREFERENCE: 'unreference',
} as const;

export type CarrierCascadeAction = (typeof CARRIER_CASCADE_ACTION)[keyof typeof CARRIER_CASCADE_ACTION];

export interface CarrierActiveRelationDriver {
  id: string;
  name: string;
}

export interface CarrierActiveRelationVehicle {
  id: string;
  licensePlate: string;
}

/** Active drivers/vehicles still referencing a carrier for a given action's scope — used for the `prevent`-mode 409 payload. */
export interface CarrierActiveRelations {
  drivers: CarrierActiveRelationDriver[];
  vehicles: CarrierActiveRelationVehicle[];
}

// ─── Sync resource descriptor (declaration-merged with the Carrier interface) ─

/**
 * Runtime descriptor for Carrier collection synchronization.
 * Use `@SyncSource(Carrier)` on a service to publish create/update/delete events.
 * Derived topics: on-carrier-created / on-carrier-updated / on-carrier-deleted
 */
export const Carrier: SyncResource<Carrier> = {
  collection: 'carriers',
  name: 'carrier',
};
